import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CHAPTERS, IMAGES } from "../../data/velvet";
import { MaskLine, FadeUp, Overline } from "./Reveal";

export const Manifesto = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section
      id="manifesto"
      data-testid="manifesto-section"
      className="relative mx-auto max-w-[1600px] px-6 py-28 md:px-16 md:py-40 lg:px-24"
    >
      <div className="mb-16 flex items-center gap-4 md:mb-24">
        <Overline>The Testament</Overline>
        <div className="vt-hairline flex-1" />
      </div>

      <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:gap-20">
        <div className="lg:col-span-7">
          <h2 className="font-serif text-4xl font-light leading-[1.05] tracking-tight text-vt-cream sm:text-5xl md:text-6xl lg:text-[4.4rem]">
            <MaskLine>We build a soul universe,</MaskLine>
            <MaskLine delay={0.08} className="italic text-vt-gold">
              one record at a time.
            </MaskLine>
          </h2>

          <FadeUp delay={0.15} className="mt-10 max-w-xl">
            <p className="font-body text-lg font-light leading-relaxed tracking-wide text-vt-ash">
              The Velvet Testament is a timeless soul universe built around
              original stories and recurring voices. No trends. No committees.
              Only a catalogue that reads like a novel — warm, patient, and built to last.
            </p>
          </FadeUp>

          <div ref={ref} className="relative mt-16 overflow-hidden rounded-sm lg:mt-24">
            <motion.img
              style={{ y: imgY, scale: 1.18 }}
              src={IMAGES.studio}
              alt="A dimly lit recording studio bathed in warm amber light"
              className="h-[38vh] w-full object-cover grayscale-[0.35] transition-[filter] duration-700 hover:grayscale-0 md:h-[52vh]"
            />
          </div>
        </div>

        <div className="flex flex-col justify-end gap-12 lg:col-span-5">
          {CHAPTERS.map((c, i) => (
            <FadeUp key={c.no} delay={i * 0.1}>
              <div className="border-t border-vt-gold/15 pt-6">
                <div className="flex items-baseline gap-4">
                  <span className="font-mono text-sm text-vt-gold">{c.no}</span>
                  <h3 className="font-serif text-2xl font-medium tracking-tight text-vt-cream md:text-3xl">
                    {c.title}
                  </h3>
                </div>
                <p className="mt-4 font-body text-base font-light leading-relaxed tracking-wide text-vt-ash">
                  {c.body}
                </p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
};
